import { ArrowRight, Clock, ExternalLink } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import type { Post } from "@/shared/lib/types"
import { cn } from "@/shared/lib/utils"
import { HapticLink } from "@/shared/ui/haptic-link"
import { TelegramMarkdown } from "@/shared/ui/telegram-markdown"
import { POST_TYPES } from "../model/post-constants"
import { PostCardMedia } from "./post-card-media"

interface PostCardProps {
	post: Post
	viewedAt?: string
	progress?: number
	actions?: ReactNode
}

const MAX_TAGS = 2

const pill =
	"inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-95"

export function PostCard({ post, viewedAt, progress, actions }: PostCardProps) {
	const isTextPost = post.type === POST_TYPES.TEXT
	const [isExpanded, setIsExpanded] = useState(false)
	const { triggerHaptic } = useHaptic()
	const hasRenderableMedia = Boolean(
		post.media?.some((m) => ["video", "video_note", "preview", "photo"].includes(m.type)),
	)
	const format = post.formats[0]?.name
	const duration = post.durations[0]?.name
	const tags = [...post.niches, ...post.topics]
	const showProgress = progress !== undefined && progress > 5 && progress < 95

	const media = hasRenderableMedia ? <PostCardMedia media={post.media} title={post.title} /> : null

	return (
		<article className="relative overflow-hidden rounded-3xl bg-card ring-1 ring-white/10">
			{actions ? <div className="absolute top-2 right-2 z-10 flex gap-1">{actions}</div> : null}

			{media && !isTextPost ? <HapticLink to={`/post/${post.id}`}>{media}</HapticLink> : media}

			<div className={cn("p-4 sm:p-5", !media && actions && "pr-14")}>
				{(format || duration) && (
					<div className="mb-1.5 flex items-center justify-between gap-3">
						<p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
							{format}
						</p>
						{duration && (
							<span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
								<Clock className="size-3.5" />
								{duration}
							</span>
						)}
					</div>
				)}

				{isTextPost ? (
					post.description && (
						<div className="space-y-1">
							<div
								className={cn(
									"whitespace-pre-wrap text-[15px] leading-relaxed",
									!isExpanded && "line-clamp-4",
								)}
							>
								<TelegramMarkdown>{post.description}</TelegramMarkdown>
							</div>
							{post.description.length > 150 && (
								<button
									type="button"
									className="text-sm font-medium text-primary"
									onClick={() => {
										triggerHaptic("light")
										setIsExpanded(!isExpanded)
									}}
								>
									{isExpanded ? "Скрыть" : "Подробнее"}
								</button>
							)}
						</div>
					)
				) : (
					<HapticLink to={`/post/${post.id}`} className="block">
						<h3 className="line-clamp-2 break-words text-xl font-bold leading-tight tracking-tight">
							{post.title}
						</h3>
						{(post.shortDescription || post.description) && (
							<p className="mt-1.5 line-clamp-2 text-sm leading-snug text-muted-foreground">
								<TelegramMarkdown>
									{post.shortDescription || post.description || ""}
								</TelegramMarkdown>
							</p>
						)}
					</HapticLink>
				)}

				{(tags.length > 0 || viewedAt || showProgress) && (
					<div className="mt-3 flex flex-wrap gap-1.5">
						{tags.slice(0, MAX_TAGS).map((tag) => (
							<span
								key={tag.id}
								className="rounded-full bg-white/[0.06] px-2.5 py-1 text-xs text-muted-foreground"
							>
								{tag.name}
							</span>
						))}
						{tags.length > MAX_TAGS && (
							<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-xs text-muted-foreground">
								+{tags.length - MAX_TAGS}
							</span>
						)}
						{viewedAt && (
							<span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-2.5 py-1 text-xs text-muted-foreground">
								<Clock className="size-3" />
								{viewedAt}
							</span>
						)}
						{showProgress && (
							<span className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary">
								Просмотрено {progress}%
							</span>
						)}
					</div>
				)}

				{!isTextPost && (
					<HapticLink
						to={`/post/${post.id}`}
						className={cn(
							pill,
							"mt-4 bg-primary text-primary-foreground shadow-lg shadow-primary/25",
						)}
					>
						Смотреть
						<ArrowRight className="size-4" />
					</HapticLink>
				)}
				{isTextPost && post.postUrl && (
					<a
						href={post.postUrl}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => triggerHaptic("medium")}
						className={cn(
							pill,
							"mt-4 bg-primary text-primary-foreground shadow-lg shadow-primary/25",
						)}
					>
						Открыть в Telegram
						<ExternalLink className="size-4" />
					</a>
				)}
			</div>
		</article>
	)
}
